import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { CLOUD_LINE } from '@/constants/aerive'

const WISPS = '/images/aerive/cloud-wisps.png'

// The fixed sky behind the whole ascent. Every cloud layer blends with the sky inside
// this one isolated layer, which is why they all live here and are driven by the
// scenes above through their data-sky names.
const SkyLayer: FC = (): ReactNode => {
    return (
        <div
            data-sky-root
            aria-hidden='true'
            className='sky-root pointer-events-none fixed inset-0 z-0 isolate overflow-hidden bg-obsidian'
        >
            <div data-sky='zoom' className='absolute inset-0 will-change-transform'>
                <div data-sky='parallax' className='absolute -inset-[3%]'>
                    <Image
                        src='/images/aerive/sky-dusk.png'
                        alt=''
                        fill
                        priority
                        sizes='106vw'
                        quality={85}
                        className='object-cover object-[50%_46%]'
                    />
                </div>
            </div>

            <div data-sky='drift' className='absolute inset-x-0 top-[14%] h-[48%] opacity-60 mix-blend-screen'>
                <div className='sky-drift-track absolute inset-y-0 left-0 flex w-[400vw]'>
                    {[false, true, false, true].map((mirrored, index) => (
                        <div key={index} className='relative h-full w-[100vw]'>
                            <Image
                                src={WISPS}
                                alt=''
                                fill
                                sizes='100vw'
                                quality={70}
                                loading='eager'
                                className='object-cover'
                                style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div data-sky='sweep' className='absolute -left-[40%] top-[8%] h-[84%] w-[180%] opacity-0 mix-blend-screen'>
                <Image
                    src={WISPS}
                    alt=''
                    fill
                    sizes='180vw'
                    quality={70}
                    loading='eager'
                    className='object-cover'
                />
            </div>

            <div data-sky='scrim' className='absolute inset-0 bg-linear-to-b from-obsidian/35 via-obsidian/45 to-obsidian/75 opacity-0' />

            <div data-sky='bank' className='absolute -left-[15%] bottom-0 h-[118%] w-[130%] opacity-0 mix-blend-screen'>
                <Image
                    src='/images/aerive/cloud-bank.png'
                    alt=''
                    fill
                    sizes='130vw'
                    quality={80}
                    loading='eager'
                    className='object-cover object-top'
                />
            </div>

            <div data-sky='veil' className='absolute inset-0 bg-ivory opacity-0' />

            <p
                data-sky='whisper'
                className='absolute inset-x-6 top-1/2 -mt-[0.6em] text-center font-heading text-[clamp(2.2rem,5vw,4.8rem)] font-light leading-[1.05] text-obsidian opacity-0'
            >
                {CLOUD_LINE}
            </p>
        </div>
    )
}

export default SkyLayer
